/**
 * Phase 5-B — PulseProvider.
 *
 * Owns Pulse's cross-page state:
 *  - drawerOpen: whether PulseDrawer is visible
 *  - messages: in-memory session chat log (NOT persisted server-side; wiped
 *              on reload / logout per brief)
 *  - tipMuted: localStorage-backed proactive-tooltip mute flag
 *  - context:  { asset_id, niche, platforms, last_title, last_caption }
 *              Pages push into this so Pulse's chat has fresh context
 *              without the drawer having to reach into page internals.
 *
 *  Also exposes `send(message)` for free-form chat (2 NXT) and
 *  `sendQuickAction(action, extras)` for the 5 chip actions. "Best time to
 *  post" bypasses the chat endpoint and calls /api/agent2/recommend
 *  (5 NXT total via the via_pulse flag) then injects a Pulse bubble
 *  client-side with no second LLM call.
 */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import { useSession } from "@/contexts/SessionContext";
import { api } from "@/lib/api";

const MUTE_KEY_PREFIX = "smartpublish_pulse_tipmuted_";

const PulseCtx = createContext(null);

function uid() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function PulseProvider({ children }) {
    const { user, balance, setBalanceFast } = useSession();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [sending, setSending] = useState(false);
    const [context, setContextState] = useState({
        asset_id: null,
        niche: null,
        platforms: [],
        last_title: null,
        last_caption: null,
    });
    const [tipMuted, setTipMutedState] = useState(false);

    const userId = user?.user_id || null;
    const muteKey = userId ? `${MUTE_KEY_PREFIX}${userId}` : null;

    // Hydrate mute pref per user.
    useEffect(() => {
        if (!muteKey) return;
        try {
            const raw = window.localStorage.getItem(muteKey);
            setTipMutedState(raw === "true");
        } catch {
            /* ignore */
        }
    }, [muteKey]);

    const setTipMuted = useCallback(
        (next) => {
            setTipMutedState(next);
            if (muteKey) {
                try {
                    window.localStorage.setItem(muteKey, String(next));
                } catch {
                    /* ignore */
                }
            }
        },
        [muteKey]
    );

    // Shallow-merge context so callers don't blow away keys they don't own.
    const setContext = useCallback((patch) => {
        setContextState((prev) => ({ ...prev, ...patch }));
    }, []);

    const openDrawer = useCallback(() => setDrawerOpen(true), []);
    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    // Reset chat on logout (user flips to null)
    const lastUserRef = useRef(userId);
    useEffect(() => {
        if (lastUserRef.current && lastUserRef.current !== userId) {
            setMessages([]);
            setContextState({
                asset_id: null,
                niche: null,
                platforms: [],
                last_title: null,
                last_caption: null,
            });
        }
        lastUserRef.current = userId;
    }, [userId]);

    const pushMessage = useCallback((msg) => {
        setMessages((prev) => [...prev, msg]);
    }, []);

    // ---- Free-form chat (2 NXT per message) ----
    const send = useCallback(
        async (text) => {
            const trimmed = (text || "").trim();
            if (!trimmed || sending) return;
            setSending(true);
            const userMsg = {
                id: uid(),
                role: "user",
                content: trimmed,
                created_at: new Date().toISOString(),
            };
            pushMessage(userMsg);

            // Compact rolling-window of the last 6 turns, excluding the one
            // we just pushed so the backend sees "history + current message".
            const recent = messages.slice(-6).map((m) => ({
                role: m.role === "user" ? "user" : "pulse",
                content: m.content,
            }));

            try {
                const { data } = await api.post("/pulse/chat", {
                    message: trimmed,
                    context: {
                        asset_id: context.asset_id || null,
                        current_niche: context.niche || null,
                        current_platform_set: context.platforms || [],
                        last_title: context.last_title || null,
                        last_caption: context.last_caption || null,
                        recent_messages: recent,
                    },
                });
                if (typeof data.balance_after === "number") {
                    setBalanceFast(data.balance_after);
                }
                pushMessage({
                    id: data.message_id || uid(),
                    role: "pulse",
                    content: data.reply,
                    followups: data.suggested_followups || [],
                    created_at:
                        data.created_at || new Date().toISOString(),
                });
            } catch (err) {
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail;
                if (status === 402) {
                    toast.error("Not enough NXT", {
                        description:
                            typeof detail === "object"
                                ? `${detail.required} required, ${detail.balance} available.`
                                : "Top up or upgrade to continue.",
                    });
                } else if (status === 403) {
                    // central interceptor opens SubscriptionDrawer
                } else {
                    pushMessage({
                        id: uid(),
                        role: "pulse",
                        content:
                            "I hit a snag reaching the model — try again in a sec.",
                        error: true,
                        created_at: new Date().toISOString(),
                    });
                }
            } finally {
                setSending(false);
            }
        },
        [sending, messages, context, pushMessage, setBalanceFast]
    );

    // ---- Quick actions ----
    // "best-time-to-post" is the Pro-only one that reuses Agent 2 (5 NXT)
    // and renders the result client-side as a Pulse bubble (no 2nd LLM call).
    const sendQuickAction = useCallback(
        async (action) => {
            if (sending) return;

            if (action === "best-time-to-post") {
                if (!context.asset_id) {
                    toast.error("No asset selected yet.", {
                        description:
                            "Upload or analyze an asset first so I know what we're routing.",
                    });
                    return;
                }
                const label = "Where & when should I post this?";
                pushMessage({
                    id: uid(),
                    role: "user",
                    content: label,
                    created_at: new Date().toISOString(),
                });
                setSending(true);
                try {
                    const { data } = await api.post("/agent2/recommend", {
                        asset_id: context.asset_id,
                        platforms:
                            context.platforms && context.platforms.length
                                ? context.platforms
                                : undefined,
                        via_pulse: true,
                    });
                    if (typeof data.balance_after === "number") {
                        setBalanceFast(data.balance_after);
                    }
                    const best = data.best_overall;
                    const lines = [
                        best
                            ? `Here's your best slot: **${best.platform} · ${best.day_of_week} at ${best.time_local}**.`
                            : "Here are the slots I'd prioritize:",
                        "",
                        ...(data.recommendations || [])
                            .slice(0, 3)
                            .map(
                                (r) =>
                                    `• **${r.platform}** — ${r.day_of_week} at ${r.time_local} _(${r.confidence})_: ${r.rationale}`
                            ),
                    ];
                    pushMessage({
                        id: uid(),
                        role: "pulse",
                        content: lines.join("\n"),
                        followups: [
                            "What hook should I open with for this slot?",
                            "Should I pin the first comment?",
                            "What's trending for my niche right now?",
                        ],
                        created_at: new Date().toISOString(),
                        kind: "agent2_best_time",
                    });
                } catch (err) {
                    const status = err?.response?.status;
                    const detail = err?.response?.data?.detail;
                    if (status === 402) {
                        toast.error("Not enough NXT for Agent 2", {
                            description: "Top up to use Best Time to Post.",
                        });
                    } else if (status === 403) {
                        // central interceptor opens SubscriptionDrawer
                    } else {
                        pushMessage({
                            id: uid(),
                            role: "pulse",
                            content:
                                typeof detail === "string"
                                    ? detail
                                    : "Agent 2 is unreachable right now — try again shortly.",
                            error: true,
                            created_at: new Date().toISOString(),
                        });
                    }
                } finally {
                    setSending(false);
                }
                return;
            }

            // Everything else is a canned prompt into free-form chat.
            const CANNED = {
                "where-to-post":
                    "Where should I post this content for maximum reach?",
                "why-tuesday":
                    "Why is Tuesday a good day for posting? And when is Tuesday wrong?",
                "suggest-hashtags": context.asset_id
                    ? "Given my current asset context, suggest 8-12 hashtags that would actually move the needle (not generic #fyp spam)."
                    : "Suggest 8-12 hashtags for my niche that actually move the needle (not generic #fyp spam).",
                "improve-last-title": context.last_title
                    ? `Improve this title for stop-the-scroll: "${context.last_title}". Give 3 variants ranked.`
                    : "Give me 3 title formulas that work across TikTok and Instagram for my niche.",
                "whats-trending": context.niche
                    ? `What's trending in the ${context.niche} niche this week? Be specific — formats, hooks, sounds.`
                    : "What's trending across TikTok, Instagram and Shorts this week?",
            };
            const msg = CANNED[action] || null;
            if (msg) {
                await send(msg);
            }
        },
        [sending, context, pushMessage, send, setBalanceFast]
    );

    const value = useMemo(
        () => ({
            drawerOpen,
            openDrawer,
            closeDrawer,
            setDrawerOpen,
            messages,
            sending,
            send,
            sendQuickAction,
            context,
            setContext,
            balance,
            tipMuted,
            setTipMuted,
        }),
        [
            drawerOpen,
            openDrawer,
            closeDrawer,
            messages,
            sending,
            send,
            sendQuickAction,
            context,
            setContext,
            balance,
            tipMuted,
            setTipMuted,
        ]
    );

    return <PulseCtx.Provider value={value}>{children}</PulseCtx.Provider>;
}

export function usePulse() {
    const ctx = useContext(PulseCtx);
    if (!ctx) {
        throw new Error("usePulse() must be used inside <PulseProvider />");
    }
    return ctx;
}
